<template>
  <div class="cj-scrollview">
    <slot />
  </div>
</template>

<script>
import 'perfect-scrollbar/css/perfect-scrollbar.css'

import { add, map, pipe, prop, reduce } from 'ramda'
import { tween, easing } from 'popmotion'
import PerfectScrollbar from 'perfect-scrollbar'

import { delay } from '../util/timer'

export default {
  name: 'CJScrollView',

  props: {
    snap: {
      type: String,
      default: null,
      validator: v => v === 'x' || v === 'y' || v === 'xy',
    },

    supress: {
      type: String,
      default: null,
      validator: v => v === 'x' || v === 'y',
    },

    margin: {
      type: Number,
      default: 0,
    },

    spacing: {
      type: Number,
      default: 0,
    },
  },

  data () {
    return {
      ps: null,
      xMin: null,
      xMax: null,
    }
  },

  async mounted () {
    this.ps = new PerfectScrollbar(this.$el, {
      suppressScrollX: this.supress === 'x',
      suppressScrollY: this.supress === 'y',
      useBothWheelAxes: this.supress,
    })

    if (/trident/i.test(window.navigator.userAgent)) {
      while (this.$el.scrollWidth < 500) {
        await delay(100)
      }
    }

    this.xMin = this.margin
    this.xMax = this.$el.scrollWidth - this.$el.clientWidth - this.margin

    this.scrollTo(this.xMin, 0, { duration: 0 })
  },

  beforeDestroy () {
    if (!this.ps) return

    this.ps.destroy()
    this.ps = null
  },

  computed: {
    snapPoints () {
      const diff = this.margin - this.$el.childNodes[0].childNodes[0].offsetLeft

      return map(
        pipe(prop('offsetLeft'), add(diff)),
        this.$el.childNodes[0].childNodes,
      )
    },

    snapPointNear (x) {
      return x => {
        return reduce(
          (a, b) => Math.abs(b - x) < Math.abs(a - x) ? b : a,
          null,
          this.snapPoints,
        )
      }
    },
  },

  methods: {
    update () {
      if (!this.ps) return

      this.ps.update()
    },

    scrollPrev () {
      const { scrollLeft, clientWidth } = this.$el
      const x = this.snapPointNear(scrollLeft - clientWidth - this.spacing)
      this.scrollTo(x)
    },

    scrollNext () {
      const { scrollLeft, clientWidth } = this.$el
      const x = this.snapPointNear(scrollLeft + clientWidth + this.spacing)
      this.scrollTo(x)
    },

    scrollTo (x, y = 0, opts = { }) {
      const {
        force = false,
        duration = 500,
        ease = easing.easeInOut,
      } = opts

      const { scrollLeft, scrollWidth, clientWidth } = this.$el
      const min = !force ? this.xMin : 0
      const max = !force ? this.xMax : scrollWidth - clientWidth

      x = Math.max(min, Math.min(max, x))

      if (duration) {
        tween({
          from: { x: scrollLeft },
          to: { x },
          duration,
          ease,
        })
          .start(({ x }) => { this.$el.scrollLeft = x })
      } else {
        this.$el.scrollLeft = x
      }
    },

    scrollIntoView (el) {
      const { scrollLeft, clientWidth } = this.$el
      const scrollRight = scrollLeft + clientWidth
      const left = el.offsetLeft
      const right = el.offsetLeft + el.clientWidth

      if (left >= scrollLeft && right <= scrollRight) return

      const opts = { force: true, duration: 250, ease: easing.easeOut }
      const x = left < scrollLeft
        ? left
        : right > scrollRight
          ? scrollLeft + (right - scrollRight)
          : scrollLeft

      this.scrollTo(x, 0, opts)
    },
  },
}
</script>

<style lang="stylus">
  .cj-scrollview
    .ps__rail-x
    .ps__rail-y
      &
      &.ps--clicking
      &:hover
        background-color: transparent

    .ps__thumb-x
    .ps__thumb-y
    .ps__rail-x:hover .ps__thumb-x
    .ps__rail-y:hover .ps__thumb-y
    .ps--clicking .ps__thumb-x
    .ps--clicking .ps__thumb-y
      background-color: rgba(0, 0, 0, 0.3)

    &.dark
      .ps__thumb-x
      .ps__thumb-y
      .ps__rail-x:hover .ps__thumb-x
      .ps__rail-y:hover .ps__thumb-y
      .ps--clicking .ps__thumb-x
      .ps--clicking .ps__thumb-y
        background-color: rgba(0, 0, 0, 0.8)
</style>

<style lang="stylus" scoped>
  .cj-scrollview
    position: relative
    overflow: hidden
</style>
